import { Link } from "react-router-dom";
import { Check, Baby, Waves, Video, ArrowUpRight } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { SKILLS, LESSON_PRICES, IMAGES } from "@/lib/content";

export default function Lessons() {
  return (
    <div data-testid="lessons-page">
      <PageHeader
        overline="Private Swim Lessons"
        title="One-on-one lessons,"
        italic="one incredible child."
        subtitle="One-on-one instruction in your home pool, fully customized to your child's age, ability and comfort. Available in 20 or 30-minute sessions."
        image={IMAGES.millyUnderwaterBoy}
      />

      {/* SKILLS */}
      <section className="py-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="overline mb-4" style={{ color: "var(--gold)" }}>Skills we build</p>
              <h2 className="font-display text-4xl md:text-5xl leading-tight" style={{ color: "var(--navy)" }}>
                Far more than <span className="italic">just swimming.</span>
              </h2>
              <p className="font-body font-light text-lg mt-6" style={{ color: "var(--muted)" }}>
                Lessons may include everything from first water confidence to stroke development for
                advanced swimmers — always at your child's pace.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-3">
            {SKILLS.map((s, i) => (
              <Reveal key={s} delay={i * 0.03}>
                <div className="flex items-center gap-3 p-4 rounded-xl bg-white border" style={{ borderColor: "rgba(212,175,55,0.2)" }}>
                  <span className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center" style={{ backgroundColor: "var(--navy)" }}>
                    <Check size={15} style={{ color: "var(--gold)" }} />
                  </span>
                  <span className="font-body" style={{ color: "var(--navy)" }}>{s}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* LESSON TYPES */}
      <section className="py-20" style={{ backgroundColor: "var(--sand)" }}>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Reveal><h2 className="font-display text-4xl md:text-5xl mb-12" style={{ color: "var(--navy)" }}>Lesson options</h2></Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {[{ i: Baby, t: "Infant & Toddler Water Introduction", d: "Gentle, age-appropriate water exposure focused on comfort, trust, breath control, water awareness and parent education." }, { i: Waves, t: "Technique & Confidence", d: "For swimmers who can already swim but need help improving form, body control, breathing, endurance or confidence." }, { i: Video, t: "Underwater Progress Videos", d: "Optional add-on: underwater videos to document your child's progress moment by moment." }].map((c, i) => (
              <Reveal key={c.t} delay={i * 0.1}>
                <div className="p-8 rounded-2xl bg-white border h-full" style={{ borderColor: "rgba(212,175,55,0.25)" }}>
                  <c.i size={30} style={{ color: "var(--gold)" }} />
                  <h3 className="font-display text-2xl mt-5" style={{ color: "var(--navy)" }}>{c.t}</h3>
                  <p className="font-body font-light text-sm mt-3 leading-relaxed" style={{ color: "var(--muted)" }}>{c.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="py-24" data-testid="lessons-pricing">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Reveal>
            <p className="overline mb-4" style={{ color: "var(--gold)" }}>Investment</p>
            <h2 className="font-display text-4xl md:text-5xl mb-4" style={{ color: "var(--navy)" }}>Pricing starting at</h2>
            <p className="font-body font-light text-lg mb-12 max-w-2xl" style={{ color: "var(--muted)" }}>
              Pricing is subject to change and lesson length is recommended after a consultation.
              Payment is required to reserve and secure your preferred time.
            </p>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {LESSON_PRICES.map((p, i) => (
              <Reveal key={p.lookup_key} delay={i * 0.08}>
                <div className="p-8 rounded-2xl border h-full flex flex-col" style={{ borderColor: p.featured ? "var(--gold)" : "rgba(212,175,55,0.25)", backgroundColor: p.featured ? "var(--navy)" : "white" }}>
                  {p.featured && <span className="overline mb-3" style={{ color: "var(--gold)" }}>Most transformative</span>}
                  <h3 className="font-display text-2xl" style={{ color: p.featured ? "var(--cream)" : "var(--navy)" }}>{p.name}</h3>
                  <p className="font-display text-5xl mt-4" style={{ color: p.featured ? "var(--gold)" : "var(--navy)" }}>{p.price}</p>
                  <p className="font-body font-light text-sm mt-3 flex-1" style={{ color: p.featured ? "var(--powder)" : "var(--muted)" }}>{p.note}</p>
                  <Link to="/contact" className={p.featured ? "btn-primary mt-6 !bg-[var(--gold)] !text-[var(--navy)]" : "btn-secondary mt-6"}>Reserve <ArrowUpRight size={16} /></Link>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
